import type { AgentInfo } from '@/shared/types'
import { API_BASE, API_URL, createHeaders } from './base'

// Actions
export interface Action {
  id: string
  label: string
  description: string
  category: string
  icon: string
  color: string
  requires_confirmation: boolean
  is_background: boolean
}

export async function getActions(): Promise<Action[]> {
  const response = await fetch(`${API_BASE}/actions`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch actions')
  const data = await response.json()
  return data.actions || []
}

export async function getAgents(): Promise<AgentInfo[]> {
  const response = await fetch(`${API_BASE}/agents`, {
    headers: createHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch agents')
  const data = await response.json()
  return data.agents || []
}

export async function executeAction(
  actionId: string,
  params?: Record<string, unknown>
): Promise<{ success: boolean; action_id: string; result?: Record<string, unknown>; error?: string }> {
  const response = await fetch(`${API_BASE}/actions/${actionId}/execute`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(params || {}),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to execute action')
  }
  return response.json()
}

export async function getRunningActions(): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/actions/running`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch running actions')
  const data = await response.json()
  return data.running || {}
}

export async function stopAction(actionId: string): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetch(`${API_BASE}/actions/${actionId}/stop`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to stop action')
  }
  return response.json()
}

export async function getActionStatus(actionId: string): Promise<{ is_running: boolean; info?: Record<string, unknown> }> {
  const response = await fetch(`${API_BASE}/actions/${actionId}/status`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch action status')
  return response.json()
}

// Agent Definitions
export type AgentType = 'scanner' | 'executor' | 'general'
export type AgentProvider = 'claude'

export interface AgentDefinition {
  id: number
  name: string
  display_name: string
  agent_type: AgentType
  provider: AgentProvider
  model_version?: string
  system_prompt?: string
  tools: string[]
  context_profile_id?: string
  is_active: boolean
  avatar?: string
  description?: string
  created_at: string
  updated_at: string
}

export interface AgentDefinitionCreate {
  name: string
  display_name: string
  agent_type: AgentType
  provider: AgentProvider
  model_version?: string
  system_prompt?: string
  tools?: string[]
  context_profile_id?: string
  is_active?: boolean
  avatar?: string
  description?: string
}

export interface AgentDefinitionUpdate {
  name?: string
  display_name?: string
  agent_type?: AgentType
  provider?: AgentProvider
  model_version?: string
  system_prompt?: string
  tools?: string[]
  context_profile_id?: string
  is_active?: boolean
  avatar?: string
  description?: string
}

export interface ProviderInfo {
  name: string
  display_name: string
  scanner_available: boolean
  executor_available: boolean
}

export interface ToolInfo {
  id: string
  name: string
  description: string
  category: string
  group: string
  type?: string
}

export async function getAgentDefinitions(
  agentType?: AgentType,
  isActive?: boolean
): Promise<AgentDefinition[]> {
  const params = new URLSearchParams()
  if (agentType) params.append('agent_type', agentType)
  if (isActive !== undefined) params.append('is_active', String(isActive))

  const url = `${API_URL}/agent-definitions${params.toString() ? `?${params}` : ''}`
  const response = await fetch(url, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch agent definitions')
  return response.json()
}

export interface AgentDefinitionWithStatus extends AgentDefinition {
  status: 'IDLE' | 'BUSY' | 'OFFLINE'
}

export async function getAgentDefinitionsWithStatus(
  agentType?: AgentType,
  isActive?: boolean
): Promise<AgentDefinitionWithStatus[]> {
  const params = new URLSearchParams()
  if (agentType) params.append('agent_type', agentType)
  if (isActive !== undefined) params.append('is_active', String(isActive))

  const url = `${API_URL}/agent-definitions/with-status${params.toString() ? `?${params}` : ''}`
  const response = await fetch(url, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch agent definitions with status')
  return response.json()
}

export async function getAgentDefinition(id: number): Promise<AgentDefinition> {
  const response = await fetch(`${API_URL}/agent-definitions/${id}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch agent definition')
  return response.json()
}

export async function getAgentDefinitionByName(name: string): Promise<AgentDefinition> {
  const response = await fetch(`${API_URL}/agent-definitions/by-name/${encodeURIComponent(name)}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch agent definition by name')
  return response.json()
}

export async function createAgentDefinition(
  data: AgentDefinitionCreate
): Promise<AgentDefinition> {
  const response = await fetch(`${API_URL}/agent-definitions`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create agent definition' }))
    throw new Error(error.detail || 'Failed to create agent definition')
  }
  return response.json()
}

export async function updateAgentDefinition(
  id: number,
  data: AgentDefinitionUpdate
): Promise<AgentDefinition> {
  const response = await fetch(`${API_URL}/agent-definitions/${id}`, {
    method: 'PUT',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update agent definition' }))
    throw new Error(error.detail || 'Failed to update agent definition')
  }
  return response.json()
}

export async function deleteAgentDefinition(id: number): Promise<void> {
  const response = await fetch(`${API_URL}/agent-definitions/${id}`, {
    method: 'DELETE',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete agent definition')
}

export async function getAgentProviders(): Promise<ProviderInfo[]> {
  const response = await fetch(`${API_URL}/agent-definitions/providers`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch providers')
  const data = await response.json()
  return data.providers
}

export async function getAgentTools(): Promise<ToolInfo[]> {
  const response = await fetch(`${API_URL}/agent-definitions/tools`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch tools')
  const data = await response.json()
  return data.tools
}

// Agent Template types
export interface AgentTemplate {
  id: string
  name: string
  description: string
  agent_type: AgentType
  provider: AgentProvider
  system_prompt: string
  tools: string[]
  category: string
  icon: string
}

export async function getAgentTemplates(): Promise<AgentTemplate[]> {
  const response = await fetch(`${API_URL}/agent-definitions/templates`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch templates')
  const data = await response.json()
  return data.templates
}

// Workspace Agent types and functions
export interface WorkspaceAgent {
  id: null
  name: string
  display_name: string
  agent_type: AgentType
  provider: AgentProvider
  model_version?: string
  system_prompt?: string
  tools: string[]
  context_profile_id: null
  is_active: boolean
  avatar: null
  description?: string
  created_at: null
  updated_at: null
  source: 'workspace'
  filename: string
}

export async function getWorkspaceAgents(workspacePath: string): Promise<WorkspaceAgent[]> {
  const params = new URLSearchParams({ workspace_path: workspacePath })
  const response = await fetch(`${API_URL}/agent-definitions/from-workspace?${params}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch workspace agents')
  return response.json()
}
